
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";


const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);
interface Option {
  label: string;
  value: string;
  email: string;
  name: string;
}
export interface UserType {
  id: string;
  name: string;
  email: string;
  phoneNumber: string;
  enabled: boolean;
  twoFactorEmailEnabled?: boolean;
  twoFactorTotpEnabled?: boolean;
  twoFactorWhatsappEnabled?: boolean;
  totpProvisionedAt?: string;
  roleId: string;
  createdDate: string;
  lastLogin: string;
  countryCode: string;
  createdBy?: string;
  updatedBy?: string;
  teamLeaderId?: string | null;
}

export interface TeamUser {
  id: string;
  name: string;
  email: string;
  enabled?: boolean;
}

export interface Team {
  leader?: TeamUser;
  unassigned: boolean;
  parentLeaderId?: string;
  directMembers: TeamUser[];
  directMemberCount: number;
  totalMemberCount: number;
}

export interface TotpProvisionResponse {
  secret: string;
  provisioningUri: string;
  issuer: string;
  account: string;
  provisionedAt: string;
}

export interface User2faSettings {
  twoFactorEmailEnabled?: boolean;
  twoFactorTotpEnabled?: boolean;
  twoFactorWhatsappEnabled?: boolean;
  totpProvisionedAt?: string;
  totpProvisioningUri?: string;
}

export interface UserHistory {
  id: string;
  snapshot: UserType & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
}

export interface UserHistoryDiff {
  historyTimestamp: string;
  mainId: number;
  id?: string;
  createdBy: string;
  snapshot: UserType & { timestamp: string }; // Add full data
  diff?: Record<string, string>;
}

export enum StatusType {
  ACTIVE = "true",
  INACTIVE = "false",
}

export async function fetchUsers(
  params: Params<UserType>
): Promise<IPageable<UserType>> {
  try {
    const response = await axiosInstance.get("/users", { params });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the users."
    );
  }
}
export async function sendReset(email: string): Promise<IPageable<UserType>> {
  try {
    const response = await axiosInstance.post(`/users/${email}/reset`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while posting the users."
    );
  }
}

export async function fetchTeams(): Promise<Team[]> {
  try {
    const response = await axiosInstance.get("/users/teams");
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the teams."
    );
  }
}

export const useTeamsQuery = (
  enabled: boolean = true
): UseQueryResult<Team[], Error> =>
  useQuery({
    queryKey: ["teams"],
    queryFn: fetchTeams,
    enabled,
  });

export const fetchRoles = async () => {
  try {
    const response = await axiosInstance.get("/roles");
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the users roles."
    );
  }
};
export const fetchRoleById = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/roles/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the users roles."
    );
  }
};

export const fetchUserByEmail = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/users/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the user."
    );
  }
};

export const fetchUser2faSettings = async (
  email: string
): Promise<User2faSettings> => {
  try {
    const response = await axiosInstance.get(
      `/users/${encodeURIComponent(email)}/2fa/settings`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the user's 2FA settings."
    );
  }
};

export const updateUser2faSettings = async (
  email: string,
  settings: Pick<
    User2faSettings,
    "twoFactorEmailEnabled" | "twoFactorTotpEnabled" | "twoFactorWhatsappEnabled"
  >
): Promise<User2faSettings> => {
  try {
    const response = await axiosInstance.put(
      `/users/${encodeURIComponent(email)}/2fa/settings`,
      settings
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the user's 2FA settings."
    );
  }
};

export const createUser = async (userData: Partial<UserType>) => {
  try {
    const response = await axiosInstance.post("/users", userData);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while Adding the user."
    );
  }
};

export const updateUser = async (userData: Partial<UserType>) => {
  try {
    const response = await axiosInstance.put(
      `/users/${userData.email}`,
      userData
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the user."
    );
  }
};

export const useUsersQuery = (
  params: Params<UserType>
): UseQueryResult<IPageable<UserType>, Error> => {
   const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["user", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchUsers(effectiveParams),
  });
};

export const useUserQuery = (
  idOrEmail: string
): UseQueryResult<UserType, Error> => {
  const queryKey = ["user", idOrEmail];
  return useQuery({
    queryKey,
    queryFn: () => fetchUserByEmail(idOrEmail),
  });
};

export const useUsersByEmailQuery = (
  params?: Params<UserType>
): UseQueryResult<Option[], Error> => {
  return useQuery({
    queryKey: [
      "fetchUsersByEmail",
      params ? JSON.stringify(params) : "default",
    ],
    queryFn: async () => {
      const queryParams: Params<UserType> = {
        size: config.MAX_PAGE_SIZE,
        ...params,
        enabled: true,
      };
      const userPage: IPageable<UserType> = await fetchUsers(queryParams);
      console.log(userPage);
      return userPage.content.map((user: UserType) => ({
        label: user.name, 
        value: user.id,   
        email: user.email,
        name: user.name,
      }));
    },
  });
};

export async function fetchUserHistory(
  id: string,
  params: Params<UserHistory>
): Promise<IPageable<UserType>> {
  try {
    const response = await axiosInstance.get(`/users/history`, {
      params: { ...params ,mainId: id, sort: "historyTimestamp,desc"  },
    });
    const data = response.data;

    return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp,
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the user history.`
    );
  }
}

export const getRoleOptions = (id: string) => ({
  queryKey: ["role", id],
  queryFn: async () => {
    return await fetchRoleById(id);
  },
  enabled: !!id,
  select: (o: UserType) => o.name,
});

export const getTeamLeaderOptions = (id?: string | null) => ({
  queryKey: ["user", id],
  queryFn: async () => {
    return await fetchUserByEmail(String(id));
  },
  enabled: !!id,
  select: (o: UserType) => o.name,
});

export async function provisionUserTotp(
  email: string
): Promise<TotpProvisionResponse> {
  try {
    const response = await axiosInstance.post(
      `/users/${encodeURIComponent(email)}/2fa/totp/provision`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while provisioning TOTP."
    );
  }
}

export async function disableUserTotpProvision(
  email: string
): Promise<UserType> {
  try {
    const response = await axiosInstance.post(
      `/users/${encodeURIComponent(email)}/2fa/totp/disable`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while disabling TOTP provisioning."
    );
  }
}
